import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, useToast } from "@/components/system";
import {
  FormInput,
  FormSelect,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  browserTz,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { GlpMealFields } from "./LogMealDialog";

/**
 * Edit-mode counterpart to LogMealDialog. Loads the meal + foods array,
 * lets the user mutate items, save, or delete the row. Trigger lives in
 * TodaysMeals (and /health/nutrition meal list).
 *
 * `foods` jsonb shape in /meal is { name, qty, unit, kcal, macros: {p,c,f}, ... };
 * we render each as one editable row and recompute totals on save so the
 * NutritionRoll trigger picks up the new aggregates.
 */

type FoodItem = {
  name: string;
  qty: number;
  unit: string;
  kcal: number;
  macros: { p: number; c: number; f: number };
  food_id?: string;
  confidence?: "high" | "medium" | "low";
  source?: string;
};

type MealFull = {
  id: string;
  ts: string;
  name: string;
  foods: FoodItem[];
  kcal: number;
  macros: { p?: number; c?: number; f?: number } | null;
  notes: string | null;
  source: string | null;
  hunger_before: number | null;
  felt_nauseous: boolean | null;
};

const MEAL_TYPES = [
  { value: "manual", label: "—" },
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "snack", label: "Snack" },
];

export function EditMealDialog({
  mealId,
  open,
  onClose,
}: {
  mealId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const profile = useAthleteProfile();
  const onGlp1 = Boolean(profile.data?.on_glp1);

  const meal = useQuery({
    queryKey: ["meal_edit", mealId],
    enabled: open && Boolean(mealId),
    staleTime: 0,
    queryFn: async (): Promise<MealFull | null> => {
      if (!mealId) return null;
      const { data, error } = await supabase
        .from("meal")
        .select("id, ts, name, foods, kcal, macros, notes, source, hunger_before, felt_nauseous")
        .eq("id", mealId)
        .maybeSingle();
      if (error) throw error;
      return data as MealFull | null;
    },
  });

  const [name, setName] = useState("");
  const [ts, setTs] = useState("");
  const [items, setItems] = useState<FoodItem[]>([]);
  const [mealType, setMealType] = useState<string>("manual");
  const [hungerBefore, setHungerBefore] = useState<string>("");
  const [feltNauseous, setFeltNauseous] = useState<boolean | null>(null);

  useEffect(() => {
    if (!meal.data) return;
    const m = meal.data;
    setName(m.name ?? "");
    setTs(toLocalDatetime(m.ts));
    setItems(
      Array.isArray(m.foods)
        ? m.foods.map((f) => ({
            name: f.name ?? "",
            qty: Number(f.qty ?? 0),
            unit: f.unit ?? "g",
            kcal: Number(f.kcal ?? 0),
            macros: {
              p: Number(f.macros?.p ?? 0),
              c: Number(f.macros?.c ?? 0),
              f: Number(f.macros?.f ?? 0),
            },
            food_id: f.food_id,
            confidence: f.confidence,
            source: f.source,
          }))
        : [],
    );
    setMealType(m.source ?? "manual");
    setHungerBefore(m.hunger_before == null ? "" : String(m.hunger_before));
    setFeltNauseous(m.felt_nauseous);
  }, [meal.data]);

  const totals = items.reduce(
    (acc, it) => ({
      kcal: acc.kcal + it.kcal,
      p: acc.p + it.macros.p,
      c: acc.c + it.macros.c,
      f: acc.f + it.macros.f,
    }),
    { kcal: 0, p: 0, c: 0, f: 0 },
  );

  const save = useMutation({
    mutationFn: async () => {
      if (!mealId || !userId) throw new Error("Missing context");
      const { error } = await supabase
        .from("meal")
        .update({
          name: name.trim() || "Meal",
          ts: new Date(ts).toISOString(),
          tz: browserTz(),
          foods: items,
          kcal: totals.kcal,
          macros: { p: totals.p, c: totals.c, f: totals.f },
          hunger_before:
            onGlp1 && hungerBefore !== "" ? Number(hungerBefore) : null,
          felt_nauseous: onGlp1 ? feltNauseous : null,
        })
        .eq("id", mealId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["today_meals", userId] });
      void qc.invalidateQueries({ queryKey: ["nutrition_roll", userId] });
      void qc.invalidateQueries({ queryKey: ["meal_edit", mealId] });
      toast.show("Meal updated.");
      onClose();
    },
    onError: (e) => toast.show(`Couldn't save: ${formatError(e)}`),
  });

  const del = useMutation({
    mutationFn: async () => {
      if (!mealId) throw new Error("Missing meal");
      const { error } = await supabase.from("meal").delete().eq("id", mealId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["today_meals", userId] });
      void qc.invalidateQueries({ queryKey: ["nutrition_roll", userId] });
      toast.show("Meal deleted.");
      onClose();
    },
    onError: (e) => toast.show(`Couldn't delete: ${formatError(e)}`),
  });

  return (
    <Drawer open={open} onClose={onClose} title="Edit meal" size="lg">
      {meal.isLoading ? (
        <p style={{ color: "var(--fg-3)" }}>Loading…</p>
      ) : !meal.data ? (
        <p style={{ color: "var(--fg-3)" }}>Meal not found.</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          <FormInput label="Meal name" value={name} onChange={setName} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
            <FormInput label="When" type="datetime-local" value={ts} onChange={setTs} />
            <FormSelect
              label="Meal type"
              value={mealType}
              onChange={setMealType}
              options={MEAL_TYPES}
            />
          </div>

          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            <span>{items.length} item{items.length === 1 ? "" : "s"}</span>
            <button
              type="button"
              onClick={() =>
                setItems((xs) => [
                  ...xs,
                  { name: "", qty: 1, unit: "g", kcal: 0, macros: { p: 0, c: 0, f: 0 } },
                ])
              }
              style={{
                background: "transparent",
                color: "var(--accent)",
                border: "none",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "var(--type-meta)",
                cursor: "pointer",
              }}
            >
              <Plus size={12} strokeWidth={1.8} /> Add item
            </button>
          </header>

          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-2)" }}>
            {items.map((it, i) => (
              <li
                key={i}
                style={{
                  padding: "var(--space-3)",
                  background: "var(--bg-2)",
                  border: "1px solid var(--hair)",
                  borderRadius: "var(--radius-card)",
                  display: "grid",
                  gridTemplateColumns: "1fr 28px",
                  gap: "var(--space-2)",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                  <input
                    value={it.name}
                    onChange={(e) =>
                      setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))
                    }
                    placeholder="Item name"
                    style={cellInput(true)}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "60px 60px 60px 60px 60px", gap: 6 }}>
                    <Cell label="qty" value={it.qty} onChange={(v) => updateItem(setItems, i, { qty: v })} />
                    <Cell label="kcal" value={it.kcal} onChange={(v) => updateItem(setItems, i, { kcal: v })} />
                    <Cell
                      label="P"
                      value={it.macros.p}
                      onChange={(v) => updateItem(setItems, i, { macros: { ...it.macros, p: v } })}
                    />
                    <Cell
                      label="C"
                      value={it.macros.c}
                      onChange={(v) => updateItem(setItems, i, { macros: { ...it.macros, c: v } })}
                    />
                    <Cell
                      label="F"
                      value={it.macros.f}
                      onChange={(v) => updateItem(setItems, i, { macros: { ...it.macros, f: v } })}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Remove item"
                  onClick={() => setItems((xs) => xs.filter((_, idx) => idx !== i))}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--fg-3)",
                    cursor: "pointer",
                    padding: 4,
                    alignSelf: "start",
                  }}
                >
                  <X size={14} strokeWidth={1.5} />
                </button>
              </li>
            ))}
          </ul>

          <div
            style={{
              padding: "var(--space-3)",
              background: "var(--bg-3)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              color: "var(--fg-2)",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Total</span>
            <span className="mono" style={{ color: "var(--fg)" }}>
              {Math.round(totals.kcal)} kcal · {Math.round(totals.p)}P / {Math.round(totals.c)}C / {Math.round(totals.f)}F
            </span>
          </div>

          {onGlp1 && (
            <GlpMealFields
              hungerBefore={hungerBefore}
              setHungerBefore={setHungerBefore}
              feltNauseous={feltNauseous}
              setFeltNauseous={setFeltNauseous}
            />
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                if (confirm("Delete this meal?")) del.mutate();
              }}
              disabled={del.isPending}
              style={{
                height: 38,
                padding: "0 var(--space-4)",
                background: "transparent",
                color: "var(--brick)",
                fontSize: "var(--type-body-s)",
                borderRadius: "var(--radius-card)",
                border: "1px solid var(--hair-2)",
                cursor: "pointer",
                marginRight: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Trash2 size={14} strokeWidth={1.5} />
              {del.isPending ? "Deleting…" : "Delete"}
            </button>
            <SecondaryButton onClick={onClose} disabled={save.isPending}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </PrimaryButton>
          </DialogFooter>
        </form>
      )}
    </Drawer>
  );
}

function updateItem(setItems: React.Dispatch<React.SetStateAction<FoodItem[]>>, i: number, patch: Partial<FoodItem>) {
  setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
}

function Cell({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontSize: "var(--type-micro)",
          color: "var(--fg-3)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <input
        inputMode="decimal"
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        style={cellInput()}
      />
    </label>
  );
}

function cellInput(prominent = false): React.CSSProperties {
  return {
    background: "var(--bg-3)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-chip)",
    color: "var(--fg)",
    fontSize: prominent ? "var(--type-body)" : "var(--type-body-s)",
    fontWeight: prominent ? 500 : 400,
    padding: prominent ? "6px 10px" : "4px 8px",
    width: "100%",
    minWidth: 0,
  };
}

function toLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}
